import { useRef, useState } from 'react';
import {
  Award,
  CheckCircle2,
  CreditCard,
  Layers,
  MapPin,
  Wrench,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';

const pillarsByLanguage = {
  en: [
    {
      id: 1,
      title: 'Brand Authority',
      openTitleLine1: 'Brand',
      openTitleLine2: 'Authority',
      subtitle: 'Official Partnership',
      teaser:
        'Authorized representation for international leaders like JAC. We bridge the gap between global manufacturing standards and local operational needs.',
      icon: Award,
      stats: '100% Genuine',
    },
    {
      id: 2,
      title: 'Market Expertise',
      openTitleLine1: 'Market',
      openTitleLine2: 'Expertise',
      subtitle: 'Geographical Insight',
      teaser:
        'Deep-rooted understanding of the North African industrial landscape. Our solutions are engineered for specific soil dynamics and climatic stress.',
      icon: MapPin,
      stats: 'Local Leadership',
    },
    {
      id: 3,
      title: 'Full Lifecycle',
      openTitleLine1: 'Full',
      openTitleLine2: 'Lifecycle',
      subtitle: 'End-to-End Solutions',
      teaser:
        'Comprehensive equipment ecosystem. From strategic acquisition to decommissioning, we manage the entire value chain of your fleet.',
      icon: Layers,
      stats: 'Full Spectrum',
    },
    {
      id: 4,
      title: 'Technical Precision',
      openTitleLine1: 'Technical',
      openTitleLine2: 'Precision',
      subtitle: 'Support Infrastructure',
      teaser:
        'Advanced diagnostic capabilities and factory-certified engineering teams. We prioritize zero-downtime maintenance protocols.',
      icon: Wrench,
      stats: '24/7 Response',
    },
    {
      id: 5,
      title: 'Capital Solutions',
      openTitleLine1: 'Capital',
      openTitleLine2: 'Solutions',
      subtitle: 'Structured Finance',
      teaser:
        'Navigating complex financial requirements with ease. Strategic banking alliances designed to facilitate high-value asset acquisition.',
      icon: CreditCard,
      stats: 'Tier-1 Credit',
    },
  ],
  fr: [
    {
      id: 1,
      title: 'Autorité de Marque',
      openTitleLine1: 'Autorité de',
      openTitleLine2: 'Marque',
      subtitle: 'Partenariat Officiel',
      teaser:
        'Représentation officielle de marques internationales comme JAC. Nous relions les standards mondiaux aux besoins opérationnels locaux.',
      icon: Award,
      stats: '100% Authentique',
    },
    {
      id: 2,
      title: 'Cycle Complet',
      openTitleLine1: 'Cycle',
      openTitleLine2: 'Complet',
      subtitle: 'Solutions de bout en bout',
      teaser:
        "Un écosystème complet, de l'acquisition stratégique à la maintenance continue. Nous pilotons toute la chaîne de valeur de votre flotte.",
      icon: Layers,
      stats: 'Couverture totale',
    },
    {
      id: 3,
      title: 'Expertise Marché',
      openTitleLine1: 'Expertise',
      openTitleLine2: 'Marché',
      subtitle: 'Intelligence terrain',
      teaser:
        "Une maîtrise concrète du contexte nord-africain. Nos solutions sont calibrées pour les contraintes locales et les conditions d'exploitation.",
      icon: MapPin,
      stats: 'Leadership local',
    },
    {
      id: 4,
      title: 'Précision Technique',
      openTitleLine1: 'Précision',
      openTitleLine2: 'Technique',
      subtitle: 'Infrastructure support',
      teaser:
        'Capacités de diagnostic avancées et équipes certifiées. Notre approche vise un protocole de maintenance à zéro interruption.',
      icon: Wrench,
      stats: 'Réponse 24/7',
    },
    {
      id: 5,
      title: 'Solutions Capital',
      openTitleLine1: 'Solutions',
      openTitleLine2: 'Capital',
      subtitle: 'Financement structuré',
      teaser:
        "Simplification des besoins financiers complexes grâce à nos alliances bancaires stratégiques pour l'acquisition d'actifs industriels.",
      icon: CreditCard,
      stats: 'Crédit premium',
    },
  ],
};

const headerByLanguage = {
  en: {
    eyebrow: 'Why ETC Company',
    titleLine1: 'The standard of',
    titleLine2: 'Industrial Trust',
    description:
      "Consistently delivering competitive advantages for the world's most demanding industrial supply chains.",
    partners: 'Partners',
    uptime: 'Uptime',
    protocol: 'Global Protocol',
    button: 'Initialize Professional Inquiry',
  },
  fr: {
    eyebrow: 'Pourquoi ETC Company',
    titleLine1: 'La référence de',
    titleLine2: 'La confiance industrielle',
    description:
      "Un avantage concurrentiel durable pour les chaînes industrielles et de distribution les plus exigeantes.",
    partners: 'Partenaires',
    uptime: 'Disponibilité',
    protocol: 'Protocole Global',
    button: 'Initialiser Une Demande Pro',
  },
};

const pillarUiByLanguage = {
  en: {
    openLabel: 'Open Pillar',
    activeLabel: 'Active Pillar',
    insightLabel: 'Strategic Insight',
  },
  fr: {
    openLabel: 'Ouvrir le pilier',
    activeLabel: 'Pilier actif',
    insightLabel: 'Aperçu stratégique',
  },
};

const pillarBackgroundById = {
  1: '/accordion/1.webp',
  2: '/accordion/2.webp',
  3: '/accordion/3.webp',
  4: '/accordion/4.webp',
  5: '/accordion/5.webp',
};

export default function HomeValuePointsSection() {
  const { language } = useI18n();
  const prefersReducedMotion = useReducedMotion();
  const locale = language === 'fr' ? 'fr' : 'en';
  const pillars = pillarsByLanguage[locale];
  const content = headerByLanguage[locale];
  const pillarUi = pillarUiByLanguage[locale];
  const openTitleLeadingClass = locale === 'fr' ? '!leading-[1.08]' : '!leading-[0.98]';
  const [activeId, setActiveId] = useState(1);
  const [hoveredId, setHoveredId] = useState(null);
  const glowRef = useRef(null);
  const glowRafRef = useRef(null);

  const handleToggle = (id) => {
    setActiveId((prevId) => (prevId === id ? null : id));
  };
  const handleKeyDown = (event, id) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle(id);
    }
  };
  const handleSectionMouseMove = (event) => {
    if (glowRafRef.current) return;
    const clientX = event.clientX;
    const clientY = event.clientY;
    const rect = event.currentTarget.getBoundingClientRect();
    glowRafRef.current = requestAnimationFrame(() => {
      glowRafRef.current = null;
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      if (glowRef.current) {
        glowRef.current.style.backgroundImage = `radial-gradient(circle at ${x}% ${y}%, rgba(255,93,16,0.08) 0%, transparent 40%)`;
      }
    });
  };

  const layerTransition = prefersReducedMotion
    ? { duration: 0 }
    : { type: 'spring', stiffness: 240, damping: 30, mass: 0.85 };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.14,
        delayChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <section
      className="relative overflow-hidden bg-[var(--dash-blacker)] pt-40"
      onMouseMove={handleSectionMouseMove}
    >
      <div className="absolute inset-0 opacity-20 bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%]" />
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_15%_25%,rgba(255,93,16,0.08)_0%,transparent_40%)]" />
      </div>
      <div className="pointer-events-none absolute inset-0 z-1">
        <div
          ref={glowRef}
          className="absolute left-0 top-0 h-full w-full transition-[background-image] duration-150 ease-out"
          style={{ backgroundImage: 'radial-gradient(circle at 15% 25%, rgba(255,93,16,0.08) 0%, transparent 40%)' }}
        />
      </div>
      <div className="pointer-events-none absolute right-0 top-0 h-[600px] w-[600px] translate-x-1/4 -translate-y-1/2 rounded-full bg-[#ff5d10]/5 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[500px] w-[500px] -translate-x-1/4 translate-y-1/2 rounded-full bg-blue-500/5 blur-[100px]" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(#ffffff 0.5px, transparent 0.5px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="container relative z-10 mx-auto px-6 lg:px-16">
        <motion.div
          key={language}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
          className="mb-16 grid gap-8 lg:grid-cols-12 lg:items-end"
        >
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <motion.div variants={containerVariants} className="flex items-center gap-4">
              <motion.div variants={itemVariants} className="text-[10px] font-black uppercase tracking-[0.5em] text-[#ff5d10]">
                <AnimatedEyebrow className="mb-6">{content.eyebrow}</AnimatedEyebrow>
              </motion.div>
            </motion.div>

            <motion.h2 variants={itemVariants} className="text-4xl font-light uppercase !leading-[1.15] text-white sm:text-5xl lg:text-6xl">
              {content.titleLine1}
              <span className="block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
                {content.titleLine2}
              </span>
            </motion.h2>
          </motion.div>
          <motion.div variants={itemVariants} className="lg:col-span-5 lg:justify-self-end">
            <p className="max-w-xl text-lg font-light leading-relaxed text-slate-400 lg:text-xl">
              {content.description}
            </p>
          </motion.div>
        </motion.div>
      </div>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        className="relative left-1/2 z-10 mt-4 flex w-screen -translate-x-1/2"
      >
        <div className="relative flex min-h-[500px] w-full flex-col items-stretch lg:h-[700px] lg:flex-row">
          {pillars.map((pillar) => {
            const isActive = activeId === pillar.id;

            return (
              <motion.button
                key={pillar.id}
                type="button"
                variants={itemVariants}
                onClick={() => handleToggle(pillar.id)}
                onKeyDown={(event) => handleKeyDown(event, pillar.id)}
                onHoverStart={() => setHoveredId(pillar.id)}
                onHoverEnd={() => setHoveredId((prev) => (prev === pillar.id ? null : prev))}
                aria-expanded={isActive}
                aria-controls={`pillar-panel-${pillar.id}`}
                aria-label={`${isActive ? pillarUi.activeLabel : pillarUi.openLabel}: ${pillar.title}`}
                className={`relative group flex min-w-0 cursor-pointer flex-col overflow-hidden border text-left transition-[flex-grow,min-height,border-color,background-color,box-shadow,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] transform-gpu focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                  isActive
                    ? 'min-h-[400px] flex-[10] border-white/20 bg-white/[0.03] shadow-2xl backdrop-blur-3xl lg:min-h-0'
                    : 'min-h-[200px] flex-1 border-white/5 bg-white/[0.01] hover:border-white/10 hover:bg-white/[0.03] lg:min-h-0'
                }`}
              >
                <motion.div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-0"
                  initial={false}
                  animate={isActive ? { opacity: 0.82 } : { opacity: 0.74 }}
                  transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <ImageOnlySkeletonLoader
                    src={pillarBackgroundById[pillar.id] || '/bg/5.webp'}
                    mode="native"
                    alt={pillar.title}
                    containerClassName="h-full w-full"
                    imgClassName="h-full w-full object-cover"
                  />
                </motion.div>
                <motion.div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-[0] mix-blend-screen"
                  initial={false}
                  animate={isActive ? { opacity: 0.28 } : { opacity: 0 }}
                  transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
                <motion.div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-[1] bg-black"
                  initial={false}
                  animate={isActive ? { opacity: 0.5 } : { opacity: 0.18 }}
                  transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                />
                <motion.div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-[2]"
                  initial={false}
                  animate={isActive ? { opacity: 0.5 } : { opacity: 0 }}
                  transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    backgroundImage:
                      'linear-gradient(45deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 72%)',
                  }}
                />
                {!isActive ? (
                  <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-full">
                    <div
                      className="absolute inset-x-0 bottom-0 h-full [background-image:linear-gradient(45deg,rgba(0,0,0,0.50)_20%,rgba(0,0,0,0.00)_60%)] sm:[background-image:linear-gradient(0deg,rgba(0,0,0,0.50)_20%,rgba(0,0,0,0.00)_60%)]"
                    />
                    <div
                      className="absolute inset-x-0 bottom-0 h-full opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100 [background-image:linear-gradient(45deg,rgba(0,0,0,0.50)_0%,rgba(0,0,0,0.50)_100%)] sm:[background-image:linear-gradient(0deg,rgba(var(--dash-main-rgb),0.50)_0%,rgba(0,0,0,0.50)_100%)]"
                    />
                  </div>
                ) : null}
                <div className="absolute inset-0 z-[3] h-full w-full">
                  <motion.div
                    aria-hidden={isActive}
                    initial={false}
                    animate={
                      isActive
                        ? { opacity: 0, y: -8 }
                        : { opacity: 1, y: 0 }
                    }
                    transition={layerTransition}
                    className="pointer-events-auto relative flex h-full w-full items-center justify-start p-5 lg:p-0"
                    style={{ pointerEvents: isActive ? 'none' : 'auto' }}
                  >
                    <motion.div
                      initial={false}
                      animate={{ y: hoveredId === pillar.id ? -18 : 0 }}
                      whileTap={prefersReducedMotion ? undefined : { scale: 0.992, y: -8 }}
                      tabIndex={-1}
                      transition={
                        prefersReducedMotion
                          ? { duration: 0 }
                          : { type: 'spring', stiffness: 260, damping: 24, mass: 0.8 }
                      }
                      className="w-full lg:h-full"
                    >
                      <div className="flex items-center gap-4 whitespace-nowrap lg:hidden">
                        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[var(--dash-main)] text-[10px] font-black tracking-[0.1em] text-white/75 transition-colors duration-300 group-hover:text-white">
                          0{pillar.id}
                        </span>
                        <div className="h-6 w-px bg-white/20" />
                        <div>
                          <h3 className="text-[14px] font-black uppercase tracking-[0.16em] text-white lg:text-xs">
                            {pillar.title}
                          </h3>
                          <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-white/75">
                            {pillar.subtitle}
                          </p>
                        </div>
                      </div>

                      <div className="relative hidden h-full w-full lg:block">
                        <div className="absolute left-1/2 top-7 -translate-x-1/2 transition-transform duration-500 ease-out group-hover:translate-y-[180px]">
                          <span className="flex h-12 w-12 items-center justify-center rounded-md bg-[var(--dash-main)] text-[14px] font-black tracking-[0.1em] text-white/75 opacity-0 transition-all duration-700 group-hover:scale-100 group-hover:text-white group-hover:opacity-100">
                            0{pillar.id}
                          </span>
                        </div>

                        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 transition-transform duration-700 ease-out group-hover:-translate-y-[180px]">
                          <div className="flex items-center justify-start">
                            <div className="w-full opacity-50 transition-all duration-300 group-hover:opacity-100 [writing-mode:vertical-rl] text-left">
                              <h3 className="h-[200px] overflow-hidden text-xs font-black uppercase tracking-[0.16em] text-white">
                                {pillar.title}
                              </h3>
                              <p className="overflow-hidden text-[10px] uppercase tracking-[0.14em] text-white/75 transition-colors duration-500 group-hover:text-white/90">
                                {pillar.subtitle}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>

                  <motion.div
                    aria-hidden={!isActive}
                    id={`pillar-panel-${pillar.id}`}
                    initial={false}
                    animate={
                      isActive
                        ? { opacity: 1, y: 0, scale: 1 }
                        : { opacity: 0, y: 12 }
                    }
                    transition={layerTransition}
                    className="pointer-events-none absolute inset-0 z-20 flex h-full flex-col p-6 lg:p-16"
                    style={{ pointerEvents: isActive ? 'auto' : 'none' }}
                  >
                    <div className="flex h-full w-full flex-col justify-between gap-8 lg:w-[880px]">
                      <div className="flex items-start justify-between gap-6">
                        <div className="min-w-0">
                          <p className="mb-3 inline-flex items-center rounded-full border border-[var(--dash-other)] bg-[var(--dash-other)] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--dash-secondary)] shadow-[0_6px_18px_rgba(0,0,0,0.12)]">
                            {pillar.subtitle}
                          </p>
                          <h3 className={`text-2xl font-black uppercase tracking-tight text-white lg:text-6xl ${openTitleLeadingClass}`}>
                            <span className="block font-light">{pillar.openTitleLine1 || pillar.title}</span>
                            <span className="block text-white">{pillar.openTitleLine2 || ''}</span>
                          </h3>
                        </div>
                      </div>

                      <div className="relative z-10">
                        <motion.div
                          initial={false}
                          animate={
                            isActive
                              ? { y: 0, opacity: 1 }
                              : { y: 14, opacity: 0 }
                          }
                          transition={layerTransition}
                          className="max-w-3xl"
                        >
                          <motion.div
                            initial={false}
                            animate={{ opacity: isActive ? 1 : 0 }}
                            transition={
                              prefersReducedMotion
                                ? { duration: 0 }
                                : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }
                            }
                            className="relative mb-6 overflow-hidden rounded-2xl border border-white/10 p-5 backdrop-blur-sm lg:mb-8 lg:p-8"
                          >
                            <motion.div
                              aria-hidden="true"
                              initial={false}
                              animate={{ opacity: isActive ? 1 : 0 }}
                              transition={
                                prefersReducedMotion
                                  ? { duration: 0 }
                                  : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }
                              }
                              className="pointer-events-none absolute inset-0 bg-black/30"
                            />
                            <p className="relative z-10 text-base font-light leading-relaxed text-white lg:text-2xl lg:leading-snug">
                              {pillar.teaser}
                            </p>
                          </motion.div>

                          <div className="flex flex-wrap items-center gap-4 lg:gap-6">
                            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 lg:px-5">
                              <CheckCircle2 size={14} className="text-[var(--dash-main)]" />
                              <span className="text-[10px] font-bold uppercase tracking-widest text-white lg:text-[11px]">
                                {pillar.stats}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                      <div className="pointer-events-none absolute bottom-5 right-5 hidden items-center gap-2 rounded-full border border-white/10 bg-black/35 px-4 py-2 text-[9px] font-black uppercase tracking-[0.2em] text-white/60 lg:flex">
                        <span>0{pillar.id}</span>
                        <span className="h-1 w-1 rounded-full bg-[var(--dash-main)]" />
                      </div>
                    </div>
                  </motion.div>
                </div>

                {isActive ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 2 }}
                    className="pointer-events-none absolute inset-0"
                  >
                    <div className="absolute right-0 top-0 h-[300px] w-[300px] rounded-full bg-[#ff5d10]/5 blur-[80px] lg:h-[400px] lg:w-[400px] lg:blur-[120px]" />
                  </motion.div>
                ) : null}
                <div
                  aria-hidden="true"
                  className={`pointer-events-none absolute bottom-0 left-0 z-20 h-[5px] bg-[var(--dash-other)] transition-all duration-700 ${
                    isActive ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
}
